import React, { Suspense, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch, useLocation, matchPath } from 'react-router';
import { useSelector } from 'react-redux';

import { userSelectors } from 'reducers';
import { useSubscriptionFormSignatureStatus, useUserRoleMatchCategory } from 'utils/hooks';
import { roleCategories } from 'utils/constants';

import { ProtectedRoute } from 'routes';
import { Loader } from 'components';

const useIsPathMatched = routes => {
    const location = useLocation();

    return routes.some(route =>
        matchPath(location.pathname, {
            path: route.path,
            exact: route.exact,
            strict: route.strict || false,
        })
    );
};

const useReturnToParam = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    return params.get('returnTo');
};

export const LoggingRedirect = ({ to, origin, ...rest }) => {
    useEffect(() => {
        if (process.env.REACT_APP_ENV !== 'production') {
            if (origin) {
                console.log(`Redirecting from: ${origin}`);
            }
            console.log(`Redirecting to: ${typeof to === 'string' ? to : JSON.stringify(to)}`);
        }
    }, [to, origin]);

    return <Redirect to={to} {...rest} />;
};

LoggingRedirect.propTypes = {
    origin: PropTypes.string,
    ...Redirect.propTypes,
};

const Routes = props => {
    const { routes, fallbackPath, loggedIn } = props;
    const location = useLocation();

    const isPathMatched = useIsPathMatched(routes);
    const returnTo = useReturnToParam();
    const userIsInvestor = useUserRoleMatchCategory(roleCategories.INVESTORS);
    const userIsDistributor = useUserRoleMatchCategory([
        roleCategories.ADMIN_DISTRIBUTORS,
        roleCategories.DISTRIBUTORS,
    ]);

    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);
    const hasInProgressSubscription = useSelector(userSelectors.getInProgressSubscriptionStatus);
    const subscriberSignedBS = useSubscriptionFormSignatureStatus(tunnelSubscription);

    const renderRoute = route => {
        const routeComponent = (
            <Suspense fallback={<Loader />}>
                <route.component />
            </Suspense>
        );

        return route.protected ? (
            <ProtectedRoute
                key={route.key}
                path={route.path}
                exact={route.exact}
                component={routeComponent}
                loggedIn={loggedIn}
            />
        ) : (
            <Route
                key={route.key}
                path={route.path}
                exact={route.exact}
                render={() => routeComponent}
            />
        );
    };

    const distributorPreviewClientSpace = useMemo(
        () => currentSubscription?.distributorCompany?.documentsValidated,
        [currentSubscription]
    );

    const redirectRoute = useMemo(() => {
        if (userIsInvestor && !location.pathname.includes('conditions-generales')) {
            if (
                hasInProgressSubscription &&
                !hasFinishedSubscription &&
                subscriberSignedBS &&
                !location.pathname.includes('dashboard') &&
                !location.pathname.includes('investor-documents') &&
                !location.pathname.includes('sign-up-success') &&
                !location.pathname.includes('sign-up-error')
            ) {
                return <LoggingRedirect to="/dashboard" origin="APP" />;
            } else if (
                hasInProgressSubscription &&
                !subscriberSignedBS &&
                !hasFinishedSubscription &&
                !location.pathname.includes('sign-up') &&
                !location.pathname.includes('abandonner')
            ) {
                return <LoggingRedirect to="/sign-up" origin="APP" />;
            }
        }

        if (userIsDistributor && !location.pathname.includes('conditions-generales')) {
            if (
                !hasFinishedSubscription &&
                distributorPreviewClientSpace &&
                !location.pathname.includes('homepage') &&
                !location.pathname.includes('products') &&
                !location.pathname.includes('distributor-documents') &&
                !location.pathname.includes('distributor-users')
            ) {
                return <LoggingRedirect to="/homepage" origin="APP" />;
            }
            if (
                !hasFinishedSubscription &&
                !distributorPreviewClientSpace &&
                !location.pathname.includes('onboarding')
            ) {
                return <LoggingRedirect to="/onboarding" origin="APP" />;
            }
        }

        if ((!!fallbackPath && !isPathMatched) || !!returnTo) {
            return (
                <LoggingRedirect
                    origin="ROUTES"
                    to={returnTo ? new URL(returnTo).pathname : fallbackPath}
                />
            );
        }
    }, [
        distributorPreviewClientSpace,
        fallbackPath,
        hasFinishedSubscription,
        hasInProgressSubscription,
        isPathMatched,
        location.pathname,
        returnTo,
        subscriberSignedBS,
        userIsDistributor,
        userIsInvestor,
    ]);

    return (
        <Switch>
            {routes.map(renderRoute)}
            {redirectRoute}
        </Switch>
    );
};

export default Routes;
